import React, { useState } from "react";
import styles from "./SkinGeneratorMenu.module.scss";
import {
  CloudUpload,
  Download,
  Pause,
  Play,
  TrashCan,
} from "@carbon/icons-react";
import { useToastContext } from "@/context/ToastContext";
import { ArtWork, CreditType } from "@/models/models";

import { useCredits } from "@/context/CreditsContext";
import { useAuth } from "@/context/AuthContext";

import ButtonPrimary from "../buttons/ButtonPrimary/ButtonPrimary";
import Modal from "../Modal/Modal";
import CreditsForm from "../CreditsForm/CreditsForm";
import {
  uploadCustomSkinToMinecraft,
  uploadSkinToMinecraft,
} from "@/services/minecraft";

interface SkinGeneratorMenuProps {
  artwork: ArtWork;
  toggleAnimation: boolean;
  deleteArtwork: () => void;
  setToggleAnimation: (toggle: boolean) => void;
  onEdit?: () => void;
}

const SkinGeneratorMenu = (props: SkinGeneratorMenuProps) => {
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [uploadingToMinecraft, setUploadingToMinecraft] = useState(false);
  const { getCreditCountByCreditType } = useCredits();
  const { user, connectMinecraft } = useAuth();
  const { showSuccessAlert, showErrorAlert } = useToastContext();

  const downloadImage = async () => {
    try {
      const response = await fetch(props.artwork.image);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "skingenerator.png");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (e) {
      console.error("Download failed:", e);
    }
  };

  const handleDeleteArtwork = async () => {
    props.deleteArtwork();
  };

  const handleToggleAnimation = () => {
    props.setToggleAnimation(!props.toggleAnimation);
  };

  const handleUploadToMinecraft = async () => {
    if (!user.has_minecraft) {
      showErrorAlert("Please connect your Minecraft account first");
      return;
    }

    const skinUrl = props.artwork.image;
    const isDataUrl = skinUrl && skinUrl.startsWith("data:");

    setUploadingToMinecraft(true);

    try {
      if (!skinUrl) {
        showErrorAlert("Cannot upload: No skin image found");
        return;
      }

      if (isDataUrl) {
        const response = await uploadCustomSkinToMinecraft(skinUrl, "classic");
        if (response.data?.success) {
          showSuccessAlert("Skin uploaded to Minecraft successfully!");
        } else {
          showErrorAlert("Failed to upload skin to Minecraft");
        }
        setUploadingToMinecraft(false);
        return;
      }
      let generationId = props.artwork.id || "";

      if (!generationId && skinUrl) {
        const match = skinUrl.match(/v2_([a-f0-9-]+)_\d+_[a-f0-9-]+\.png/i);
        if (match && match[1]) {
          generationId = match[1];
        }
      }

      if (generationId) {
        const response = await uploadSkinToMinecraft(
          generationId,
          0,
          "classic"
        );

        if (response.data?.success) {
          showSuccessAlert("Skin uploaded to Minecraft successfully!");
        } else {
          showErrorAlert("Failed to upload skin to Minecraft");
        }
      } else if (skinUrl) {
        // Fallback: Try to upload by URL directly if we have a URL but no generation ID
        const response = await uploadCustomSkinToMinecraft(skinUrl, "classic");
        if (response.data?.success) {
          showSuccessAlert("Skin (from URL) uploaded successfully!");
        } else {
          showErrorAlert("Failed to upload skin");
        }
      } else {
        showErrorAlert("Cannot upload: No skin image found");
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to upload skin to Minecraft";
      showErrorAlert(errorMessage);
    } finally {
      setUploadingToMinecraft(false);
    }
  };

  const canUploadToMinecraft = Boolean(user?.has_minecraft);

  return (
    <div className={styles.menu}>
      <div className={styles.credits}>
        <p>
          <span className={styles.bold}>
            {getCreditCountByCreditType(CreditType.minecraft)}
          </span>{" "}
          <br />
          Credits Left
        </p>
        <ButtonPrimary
          loading={false}
          onClick={() => setShowCreditsModal(true)}
          text={"Buy Credits"}
          className="w-full"
        />
      </div>
      <div className={styles.actions}>
        <button
          className={`${styles.menuContentRow} ${styles.animate}`}
          onClick={handleToggleAnimation}
        >
          <span className={styles.DownloadIcon}>
            {props.toggleAnimation ? <Pause size={24} /> : <Play size={24} />}
          </span>
          <p className={styles.buttonLabel}>Animate</p>
        </button>

        <button className={styles.menuContentRow} onClick={downloadImage}>
          <span className={styles.DownloadIcon}>
            <Download size={24} />
          </span>
          <p className={styles.buttonLabel}>Download</p>
        </button>

        {canUploadToMinecraft && (
          <button
            className={styles.menuContentRow}
            onClick={handleUploadToMinecraft}
            disabled={uploadingToMinecraft}
            style={{ opacity: uploadingToMinecraft ? 0.6 : 1 }}
          >
            <span className={styles.DownloadIcon}>
              <CloudUpload size={24} />
            </span>
            <p className={styles.buttonLabel}>
              {uploadingToMinecraft ? "Uploading..." : "Set Active Skin"}
            </p>
          </button>
        )}

        <button
          className={`${styles.menuContentRow} ${styles.danger}`}
          onClick={handleDeleteArtwork}
        >
          <span className={styles.DownloadIcon}>
            <TrashCan size={24} />
          </span>
          <p className={styles.buttonLabel}>Delete</p>
        </button>
      </div>

      <Modal
        showHeader={true}
        headerContent={<h2 className="headline-4">Buy Credits</h2>}
        width={"auto"}
        height={"auto"}
        isOpen={showCreditsModal}
        toggleModal={() => setShowCreditsModal(!showCreditsModal)}
      >
        <CreditsForm />
      </Modal>
    </div>
  );
};

export default SkinGeneratorMenu;
